import React, { FC, useContext, useEffect, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useLocation } from "react-router-dom";

import { PostBillAPIContext } from "@app/provider";
import { routes } from "@app/routing";
import { BlueHeaderContent } from "@app/components/ui/layout";
import { LoadingContext } from "@natera/platform/lib/components/context";
import { PdfView } from "@app/components/pdf";
import { useBillingPdfFile } from "@app/postBill/hooks";
import { downloadPdf } from "@app/postBill/utils/viewPdf";
import { logMsg } from "@app/utils/logMsg";
import "@app/postBill/postBill.scss";

const messages = defineMessages({
  billingReceiptPdfTitle: {
    id: "billingReceiptPdfTitle",
    defaultMessage: "Receipt",
  },
});

const BillingReceiptPdf: FC = () => {
  const intl = useIntl();
  const location = useLocation();
  const { loadingStates, getReceipt, getCTSToken } =
    useContext(PostBillAPIContext);
  const paymentIntentId = location.state?.paymentIntentId ?? "";
  const ctsToken = getCTSToken?.();
  const { file, pdfError } = useBillingPdfFile({
    url: `/api/1.0/postbill/receipt/${paymentIntentId}`,
    token: ctsToken,
    authScheme: "cts-token",
  });
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<boolean>(false);

  const fetchPDF = async () => {
    try {
      const arrayBuffer = (await getReceipt(paymentIntentId)) as ArrayBuffer;
      const blob = new Blob([arrayBuffer], { type: "application/pdf" });
      setPdfBlob(blob);
    } catch {
      setError(true);
      logMsg("error", "postbill.pdf", "Failed to get pdf receipt");
    }
  };

  useEffect(() => {
    if (!paymentIntentId) return;
    fetchPDF();
  }, [paymentIntentId]);

  const onDownloadPdf = async () => {
    const fileName = intl.formatMessage(messages.billingReceiptPdfTitle);
    downloadPdf(pdfBlob, fileName);
  };

  return (
    <div className="billing-pdf billing-receipt-pdf">
      <BlueHeaderContent
        blueHeaderTitle={intl.formatMessage(messages.billingReceiptPdfTitle)}
        crossHeaderRoute={routes.billingHistoryPayments}
        fullScreen
      >
        <LoadingContext isLoading={loadingStates.getReceipt}>
          <PdfView
            file={file}
            onDownload={onDownloadPdf}
            hasError={error || pdfError}
          />
        </LoadingContext>
      </BlueHeaderContent>
    </div>
  );
};

export default BillingReceiptPdf;
